import { useCallback, useEffect, useState } from "react";
import { api, apiError, dateFmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Th, Td } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import TenantConfigTab from "@/components/settings/TenantConfigTab";
import TaxRatesTab from "@/components/settings/TaxRatesTab";
import LocationsTab from "@/components/inventory/LocationsTab";
import BackupTab from "@/components/settings/BackupTab";
import CarriersTab from "@/components/settings/CarriersTab";
import ShopifyConnectionTab from "@/components/settings/ShopifyConnectionTab";
import OperatorBridgeTab from "@/components/settings/OperatorBridgeTab";
import ProductMappingTab from "@/components/settings/ProductMappingTab";
import ShopifySync from "@/pages/Shopify";
import { useSearchParams } from "react-router-dom";
import { useI18n } from "@/i18n";

export default function SettingsPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "system";
  const isAdmin = user?.role === "admin";
  const isSuperadmin = user?.platform_role === "superadmin";
  const isPlatform = ["superadmin", "dev"].includes(user?.platform_role);
  const [settings, setSettings] = useState(null);
  const [bus, setBus] = useState([]);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [open, setOpen] = useState(false);
  const [nu, setNu] = useState({ name: "", email: "", password: "", role: "verkauf" });
  const [editU, setEditU] = useState(null);
  const [dnRange, setDnRange] = useState(null);   // Nummernkreis Lieferschein (laufendes Jahr)
  const SUPER = "arne.mentz@consberry.com";

  const load = useCallback(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/business-units").then((r) => setBus(r.data)).catch(() => {});
    api.get("/audit-logs").then((r) => setAudit(r.data)).catch(() => {});
    api.get("/tenant-config/number-range/delivery-note").then((r) => setDnRange(r.data)).catch(() => setDnRange(null));
    if (isAdmin) api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const saveSetting = async (patch) => {
    try { const r = await api.put("/settings", patch); setSettings(r.data); toast.success(t("set.saveOk")); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addUser = async () => {
    try { await api.post("/auth/users", nu); toast.success(t("set.userCreated")); setOpen(false); setNu({ name: "", email: "", password: "", role: "verkauf" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async () => {
    try { await api.patch(`/auth/users/${editU.id}`, { name: editU.name, role: editU.role, password: editU.password || undefined, active: editU.active, ...(isPlatform ? { platform_role: editU.platform_role ?? null } : {}) });
      toast.success(t("set.userUpdated")); setEditU(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delUser = async (u) => {
    if (!window.confirm(t("set.userDelConfirm", { name: u.name, email: u.email }))) return;
    try { await api.delete(`/auth/users/${u.id}`); toast.success(t("set.userDeleted")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!settings) return <div className="text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div>
      <PageHeader title={t("settings.title")} subtitle={t("settings.subtitle")} />
      <Tabs defaultValue={initialTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="system" data-testid="tab-system">{t("settings.tab.system")}</TabsTrigger>
          <TabsTrigger value="mandant" data-testid="tab-mandant">{t("settings.tab.tenant")}</TabsTrigger>
          <TabsTrigger value="tax" data-testid="tab-tax">{t("settings.tab.tax")}</TabsTrigger>
          <TabsTrigger value="bu" data-testid="tab-bu">{t("settings.tab.bu")}</TabsTrigger>
          <TabsTrigger value="plaetze" data-testid="tab-plaetze">{t("loc.tab")}</TabsTrigger>
          <TabsTrigger value="carrier" data-testid="tab-carrier">{t("carr.tab")}</TabsTrigger>
          {(isAdmin || isSuperadmin) && <TabsTrigger value="integrations" data-testid="tab-integrations">{t("settings.tab.integrations")}</TabsTrigger>}
          <TabsTrigger value="waehrung" data-testid="tab-waehrung">{t("settings.tab.currency")}</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users">{t("settings.tab.users")}</TabsTrigger>}
          <TabsTrigger value="audit" data-testid="tab-audit">{t("settings.tab.audit")}</TabsTrigger>
          {isAdmin && <TabsTrigger value="backup" data-testid="tab-backup">{t("bk.tabShort")}</TabsTrigger>}
          {isSuperadmin && <TabsTrigger value="operator" data-testid="tab-operator">Operator-Bridge</TabsTrigger>}
        </TabsList>

        <TabsContent value="mandant" className="mt-4">
          <TenantConfigTab isAdmin={isAdmin} onSaved={load} />
        </TabsContent>

        <TabsContent value="tax" className="mt-4">
          <TaxRatesTab isAdmin={isAdmin} settings={settings} onSettings={load} />
        </TabsContent>

        <TabsContent value="system" className="mt-4 max-w-xl space-y-4">
          <div className="rounded-md border border-border bg-card p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div><Label>{t("set.currency")}</Label><p className="text-xs text-muted-foreground">{t("set.currencyHint")}</p></div>
              <span className="font-mono font-semibold" data-testid="system-currency">{settings.base_currency || settings.default_currency || "EUR"}</span>
            </div>
            <div className="flex items-center justify-between">
              <div><Label>{t("set.defaultTax")}</Label><p className="text-xs text-muted-foreground">{t("set.vatPercent")}</p></div>
              <Input type="number" className="w-24" defaultValue={settings.default_tax_rate} disabled={!isAdmin}
                onBlur={(e) => isAdmin && saveSetting({ default_tax_rate: Number(e.target.value) })} data-testid="setting-tax" />
            </div>
            <div className="flex items-center justify-between">
              <div><Label>{t("set.taxRates")}</Label><p className="text-xs text-muted-foreground">{t("set.taxRatesHint", { rates: (settings.tax_rates || [0, 7, 19]).map((r) => `${r} %`).join(" · ") })}</p></div>
              <Input className="w-32" defaultValue={(settings.tax_rates || [0, 7, 19]).join(", ")} disabled={!isAdmin} data-testid="setting-tax-rates"
                onBlur={(e) => isAdmin && saveSetting({ tax_rates: e.target.value.split(",").map((x) => Number(x.trim())).filter((x) => !isNaN(x)) })} />
            </div>
            <div className="flex items-center justify-between">
              <div><Label>{t("set.negativeStock")}</Label><p className="text-xs text-muted-foreground">{t("set.negativeStockHint")}</p></div>
              <Switch checked={settings.allow_negative_stock} disabled={!isAdmin}
                onCheckedChange={(v) => saveSetting({ allow_negative_stock: v })} data-testid="setting-negative" />
            </div>
            <div className="flex items-center justify-between">
              <div><Label>{t("set.idleLogout")}</Label><p className="text-xs text-muted-foreground">{t("set.idleLogoutHint")}</p></div>
              <Input type="number" min="0" max="480" className="w-24" disabled={!isAdmin}
                defaultValue={settings.idle_logout_minutes ?? 30} data-testid="setting-idle-logout"
                onBlur={(e) => isAdmin && saveSetting({ idle_logout_minutes: Number(e.target.value) })} />
            </div>
            <div className="flex items-center justify-between">
              <div><Label>{t("set.revenueRecognition")}</Label><p className="text-xs text-muted-foreground">{t("set.revenueRecognitionHint")}</p></div>
              <span className="text-sm font-medium">{t("set.onShipment")}</span>
            </div>
            {dnRange && (
              <div className="flex items-center justify-between" data-testid="setting-dn-range">
                <div><Label>{t("set.dnNumberRange", { year: dnRange.year })}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t("set.dnNumberRangeHint", { next: dnRange.next_number, used: dnRange.highest_used })}
                  </p></div>
                <Input type="number" min="1" className="w-28" disabled={!isAdmin}
                  defaultValue={dnRange.next} data-testid="setting-dn-next"
                  onBlur={async (e) => {
                    const next = Number(e.target.value);
                    if (!isAdmin || !next || next === dnRange.next) return;
                    try {
                      const r = await api.put("/tenant-config/number-range/delivery-note",
                        { year: dnRange.year, next_number: next });
                      setDnRange(r.data);
                      toast.success(t("set.dnNumberRangeSaved", { number: r.data.next_number }));
                    } catch (err) { toast.error(apiError(err)); }
                  }} />
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="waehrung" className="mt-4 max-w-xl space-y-4">
          <div className="rounded-md border border-border bg-card p-5 space-y-4">
            <div className="text-sm font-medium">{t("set.exchangeRates")}</div>
            {["USD", "CAD"].map((c) => (
              <div key={c} className="flex items-center justify-between">
                <Label>{c} → EUR</Label>
                <Input type="number" step="0.0001" className="w-32" defaultValue={settings.exchange_rates?.[c]} disabled={!isAdmin}
                  onBlur={(e) => isAdmin && saveSetting({ exchange_rates: { ...(settings.exchange_rates || {}), [c]: Number(e.target.value) } })} data-testid={`rate-${c}`} />
              </div>
            ))}
            <div className="border-t border-border pt-3 space-y-3">
              <div><Label>{t("set.companyName")}</Label><Input defaultValue={settings.company?.name} disabled={!isAdmin} onBlur={(e) => isAdmin && saveSetting({ company: { ...(settings.company || {}), name: e.target.value } })} className="mt-1.5" data-testid="company-name" /></div>
              <div><Label>{t("common.address")}</Label><Textarea defaultValue={settings.company?.address} disabled={!isAdmin} onBlur={(e) => isAdmin && saveSetting({ company: { ...(settings.company || {}), address: e.target.value } })} className="mt-1.5" /></div>
              <div><Label>{t("set.vatId")}</Label><Input defaultValue={settings.company?.vat_id} disabled={!isAdmin} onBlur={(e) => isAdmin && saveSetting({ company: { ...(settings.company || {}), vat_id: e.target.value } })} className="mt-1.5" /></div>
              <div><Label>{t("set.invoiceNote")}</Label><Textarea defaultValue={settings.invoice_note} disabled={!isAdmin} onBlur={(e) => isAdmin && saveSetting({ invoice_note: e.target.value })} className="mt-1.5" /></div>
              <div>
                <Label>{t("set.logo")}</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  {settings.company?.logo_data_url && <img src={settings.company.logo_data_url} alt="Logo" className="h-12 w-auto rounded border border-border bg-white p-1" data-testid="logo-preview" />}
                  {isAdmin && <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted" data-testid="logo-upload-label">
                    {t("set.logoUpload")}
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="logo-file-input" onChange={(e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) { toast.error(t("set.logoType")); e.target.value = ""; return; }
                      if (file.size > 1000000) { toast.error(t("set.logoSize")); e.target.value = ""; return; }
                      const reader = new FileReader();
                      reader.onload = () => saveSetting({ company: { ...(settings.company || {}), logo_data_url: reader.result } });
                      reader.readAsDataURL(file);
                    }} />
                  </label>}
                  {isAdmin && settings.company?.logo_data_url && <Button size="sm" variant="ghost" onClick={() => saveSetting({ company: { ...(settings.company || {}), logo_data_url: "" } })} data-testid="logo-remove-btn">{t("common.remove")}</Button>}
                </div>
              </div>
              <div><Label>{t("set.lucid")}</Label><Input defaultValue={settings.lucid_id} disabled={!isAdmin} onBlur={(e) => isAdmin && saveSetting({ lucid_id: e.target.value })} className="mt-1.5" data-testid="lucid-id" /><p className="text-xs text-muted-foreground mt-1">{t("set.lucidHint")}</p></div>
              <div className="border-t border-border pt-3">
                <div className="text-sm font-medium mb-2">{t("set.letterhead")}</div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ["website", t("common.website")], ["email", t("common.email")], ["city", t("common.city")],
                    ["sender_line", "Absenderzeile"], ["footer_brand", "Footer-Zeile 1 (Marke)"],
                    ["office_address", "Büroanschrift"], ["managing_director", "Geschäftsführung"],
                    ["bank_name", t("common.bank")], ["iban", "IBAN"], ["bic", "BIC"],
                    ["register", "Handelsregister (HRB / Amtsgericht)"], ["eori", "EORI-Nr. (CN23/Zoll)"],
                  ].map(([key, label]) => (
                    <div key={key} className={["sender_line", "footer_brand", "office_address", "register"].includes(key) ? "col-span-2" : ""}>
                      <Label>{label}</Label>
                      <Input defaultValue={settings.company?.[key]} disabled={!isAdmin} className="mt-1.5" data-testid={`company-${key}`}
                        onBlur={(e) => isAdmin && saveSetting({ company: { ...(settings.company || {}), [key]: e.target.value } })} />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">{t("set.letterheadHint")}</p>
              </div>
              <div className="text-xs text-muted-foreground">{t("set.taxNote", { tab: t("settings.tab.tenant") })}</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="bu" className="mt-4">
          <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm">
            <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("common.name")}</Th><Th>{t("common.code")}</Th><Th>{t("common.status")}</Th></tr></thead>
              <tbody>{bus.map((b) => <tr key={b.id}><Td>{b.name}</Td><Td mono>{b.code}</Td><Td>{b.active ? t("common.active") : t("common.inactive")}</Td></tr>)}
                <tr><Td className="text-muted-foreground">Travel Club</Td><Td mono className="text-muted-foreground">TC</Td><Td className="text-xs text-muted-foreground">{t("set.buPrepared")}</Td></tr>
              </tbody></table>
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="backup" className="mt-4">
            <BackupTab />
          </TabsContent>)}

        {isAdmin && (
          <TabsContent value="users" className="mt-4">
            <div className="flex justify-end mb-3"><Button size="sm" onClick={() => setOpen(true)} data-testid="new-user-btn"><Plus className="h-4 w-4 mr-1.5" /> {t("common.user")}</Button></div>
            <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm">
              <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("common.name")}</Th><Th>{t("common.email")}</Th><Th>{t("common.role")}</Th><Th>{t("ten.title")}</Th><Th>{t("set.colLastLogin")}</Th><Th>{t("set.colUpdatedAt")}</Th><Th>{t("set.colPwChangedAt")}</Th><Th right>{t("common.actions")}</Th></tr></thead>
                <tbody data-testid="users-table">{users.map((u) => {
                  const isSuper = u.email.toLowerCase() === SUPER;
                  return (
                    <tr key={u.id}><Td>{u.name}</Td><Td>{u.email}</Td><Td>{t(`role.${u.role}`, u.role)}{u.active === false ? <span className="ml-2 text-xs text-destructive">{t("set.deactivated")}</span> : null}</Td>
                      <Td className={u.business_unit_id !== user?.business_unit_id ? "text-chart-3 text-xs" : "text-xs text-muted-foreground"} data-testid={`user-tenant-${u.email}`}>{bus.find((b) => b.id === u.business_unit_id)?.name || "—"}{u.business_unit_id !== user?.business_unit_id ? ` · ${t("set.foreignTenant")}` : ""}</Td>
                      <Td className="text-xs text-muted-foreground" data-testid={`user-lastlogin-${u.email}`}>{u.last_login_at ? dateFmt(u.last_login_at) : "—"}</Td>
                      <Td className="text-xs text-muted-foreground" data-testid={`user-updated-${u.email}`}>{u.updated_at ? dateFmt(u.updated_at) : "—"}</Td>
                      <Td className="text-xs text-muted-foreground" data-testid={`user-pwchanged-${u.email}`}>{u.password_changed_at ? dateFmt(u.password_changed_at) : "—"}</Td>
                      <Td right>{isSuper ? <span className="text-xs text-muted-foreground">{t("set.superProtected")}</span> : (
                        <span className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => setEditU({ id: u.id, name: u.name, role: u.role, password: "", active: u.active !== false, platform_role: u.platform_role || null })} data-testid={`edit-user-${u.email}`}>{t("common.edit")}</Button>
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => delUser(u)} data-testid={`delete-user-${u.email}`}>{t("common.delete")}</Button>
                        </span>)}</Td>
                    </tr>);
                })}</tbody></table>
            </div>
          </TabsContent>
        )}

        <TabsContent value="plaetze" className="mt-4">
          <LocationsTab />
        </TabsContent>

        <TabsContent value="carrier" className="mt-4">
          <CarriersTab isAdmin={isAdmin} />
        </TabsContent>

        {(isAdmin || isSuperadmin) && (
          <TabsContent value="integrations" className="mt-4">
            <Tabs defaultValue={isAdmin ? "conn" : "produktzuordnung"}>
              <TabsList className="flex-wrap h-auto w-fit">
                {isAdmin && <TabsTrigger value="conn" data-testid="tab-shopify-conn">{t("sh.tabConn")}</TabsTrigger>}
                {isAdmin && <TabsTrigger value="log" data-testid="tab-shopify-log">{t("sh.tabLog")}</TabsTrigger>}
                {isSuperadmin && <TabsTrigger value="produktzuordnung" data-testid="tab-produktzuordnung">Produktzuordnung</TabsTrigger>}
              </TabsList>
              {isAdmin && (
                <TabsContent value="conn" className="mt-4">
                  <ShopifyConnectionTab canEdit={isAdmin} />
                </TabsContent>)}
              {isAdmin && (
                <TabsContent value="log" className="mt-4">
                  <ShopifySync embedded />
                </TabsContent>)}
              {isSuperadmin && (
                <TabsContent value="produktzuordnung" className="mt-4">
                  <ProductMappingTab />
                </TabsContent>)}
            </Tabs>
          </TabsContent>
        )}

        {isSuperadmin && (
          <TabsContent value="operator" className="mt-4">
            <OperatorBridgeTab />
          </TabsContent>
        )}

        <TabsContent value="audit" className="mt-4">
          <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm max-h-[500px] overflow-y-auto">
            <table className="w-full"><thead className="bg-muted/50 sticky top-0"><tr><Th>{t("common.timestamp")}</Th><Th>{t("common.user")}</Th><Th>{t("common.action")}</Th><Th>{t("common.object")}</Th><Th>{t("common.note")}</Th></tr></thead>
              <tbody data-testid="audit-table">{audit.map((a) => (
                <tr key={a.id} className="hover:bg-muted/40"><Td className="text-xs">{dateFmt(a.created_at)}</Td><Td className="text-xs">{a.user_email || "System"}</Td>
                  <Td className="capitalize">{a.action}</Td><Td>{a.entity_type}</Td><Td className="text-xs text-muted-foreground">{a.note || "—"}</Td></tr>))}
                {audit.length === 0 && <tr><Td className="text-muted-foreground">{t("set.noAudit")}</Td></tr>}
              </tbody></table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("set.newUser")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("common.name")}</Label><Input value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} className="mt-1.5" data-testid="user-name" /></div>
            <div><Label>{t("common.email")}</Label><Input value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} className="mt-1.5" data-testid="user-email" /></div>
            <div><Label>{t("common.password")}</Label><Input type="password" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} className="mt-1.5" data-testid="user-password" /></div>
            <div><Label>{t("common.role")}</Label>
              <Select value={nu.role} onValueChange={(v) => setNu({ ...nu, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {isPlatform && <SelectItem value="admin">{t("role.admin")}</SelectItem>}
                  <SelectItem value="lager">{t("role.lager")}</SelectItem>
                  <SelectItem value="verkauf">{t("role.verkauf")}</SelectItem>
                  <SelectItem value="disponent">{t("role.disponent")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-xs text-muted-foreground" data-testid="user-role-hint">{t("role.disponentHint")}</p>
            </div>
            {isPlatform && (
              <div><Label>{t("role.platformRole")}</Label>
                <Select value={nu.platform_role || "none"} onValueChange={(v) => setNu({ ...nu, platform_role: v === "none" ? null : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-platform-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("role.none")}</SelectItem>
                    <SelectItem value="dev">{t("role.dev")}</SelectItem>
                    {isSuperadmin && <SelectItem value="superadmin">{t("role.superadmin")}</SelectItem>}
                  </SelectContent>
                </Select>
                <p className="mt-1.5 text-xs text-muted-foreground" data-testid="user-platform-role-hint">{t("role.devHint")}</p>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={addUser} disabled={!nu.email || !nu.password || !nu.name} data-testid="save-user-btn">{t("common.create")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editU} onOpenChange={(o) => !o && setEditU(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("set.editUser")}</DialogTitle></DialogHeader>
          {editU && <div className="space-y-3">
            <div><Label>{t("common.name")}</Label><Input value={editU.name} onChange={(e) => setEditU({ ...editU, name: e.target.value })} className="mt-1.5" data-testid="edit-user-name" /></div>
            <div><Label>{t("common.role")}</Label>
              <Select value={editU.role} onValueChange={(v) => setEditU({ ...editU, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="admin">{t("role.admin")}</SelectItem><SelectItem value="lager">{t("role.lager")}</SelectItem><SelectItem value="verkauf">{t("role.verkauf")}</SelectItem><SelectItem value="disponent">{t("role.disponent")}</SelectItem></SelectContent>
              </Select>
            </div>
            {isPlatform && (
              <div><Label>{t("role.platformRole")}</Label>
                <Select value={editU.platform_role || "none"} onValueChange={(v) => setEditU({ ...editU, platform_role: v === "none" ? null : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="edit-user-platform-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("role.none")}</SelectItem>
                    <SelectItem value="dev">{t("role.dev")}</SelectItem>
                    {isSuperadmin && <SelectItem value="superadmin">{t("role.superadmin")}</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>{t("set.newPassword")}</Label><Input type="password" value={editU.password} onChange={(e) => setEditU({ ...editU, password: e.target.value })} className="mt-1.5" data-testid="edit-user-password" placeholder={t("set.keepEmpty")} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editU.active !== false} data-testid="edit-user-active"
              onChange={(e) => setEditU({ ...editU, active: e.target.checked })} /> {t("set.accountActive")}</label>
          </div>}
          <DialogFooter><Button onClick={saveEdit} disabled={!editU?.name} data-testid="save-edit-user-btn">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
